import React, { FC, InputHTMLAttributes, RefObject } from "react";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import i18n from "../../service/i18n";
import {
  focusColor,
  progressBarBackgroundColor,
  progressBarBufferColor,
  progressBarDisabledColor,
  progressBarHoverColor
} from "../../styles/variables";

export const Progress = styled.div<{
  isMobile: boolean;
  margin?: string;
  visibility: string;
  zoomOnHover?: boolean;
}>`
  height: ${props => (props.isMobile ? "40px" : "32px")};
  margin: ${props => (props.margin ? props.margin : "0 16px")};
  opacity: ${props => (props.visibility === "hidden" ? 0 : 1)};
  position: relative;
  transition: ${props =>
    props.visibility === "hidden"
      ? "opacity 300ms, visibility 0s 300ms"
      : "opacity 300ms"};
  visibility: ${props => props.visibility};
  width: 100%;

  &:hover {
    div progress {
      height: ${props => (props.zoomOnHover ? "6px" : "3px")};
    }
  }
`;

export const ElapsedProgress = styled.progress<{ backgroundColor: string }>`
  &::-webkit-progress-value {
    background-color: ${props => props.backgroundColor};
  }

  &::-moz-progress-bar {
    background-color: ${props => props.backgroundColor};
  }
`;

export const BufferProgress = styled.progress<{ backgroundColor?: string }>`
  &::-webkit-progress-value {
    background-color: ${props =>
      props.backgroundColor || progressBarBufferColor};
  }

  &::-moz-progress-bar {
    background-color: ${props =>
      props.backgroundColor || progressBarBufferColor};
  }
`;

export const HoverProgress = styled.progress`
  &::-webkit-progress-value {
    background-color: ${progressBarHoverColor};
  }

  &::-moz-progress-bar {
    background-color: ${progressBarHoverColor};
  }
`;

export const Wrapper = styled.div<{
  backgroundColor?: string;
  zoomOnHover?: boolean;
}>`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;

  progress {
    appearance: none;
    background-color: ${props =>
      props.backgroundColor || progressBarBackgroundColor};
    border-radius: 3px;
    border: none;
    height: 3px;
    width: 100%;

    &::-webkit-progress-bar {
      background-color: ${props =>
        props.backgroundColor || progressBarBackgroundColor};
      border-radius: 3px;
    }

    &::-webkit-progress-value {
      border-radius: 3px;
    }

    &::-moz-progress-bar {
      border-radius: 3px;
    }

    &:hover {
      height: ${props => (props.zoomOnHover ? "6px" : "3px")};
    }
  }
`;

const rangeThumbStyles = css<{
  background: string;
  disabled?: boolean;
  isDesktopVideo?: boolean;
}>`
  background: ${props =>
    props.disabled ? progressBarDisabledColor : props.background};
  border: none;
  border-radius: 50%;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  height: ${props => (props.isDesktopVideo ? 9 : 16)}px;
  width: ${props => (props.isDesktopVideo ? 9 : 16)}px;
`;

const SeekSliderStyled = styled.input<{
  background: string;
  disabled?: boolean;
  isDesktopVideo?: boolean;
}>`
  appearance: none;
  border-radius: 4px;
  background: transparent;
  cursor: ${props => (props.disabled ? "not-allowed" : "pointer")};
  height: 100%;
  margin: 0;
  position: absolute;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${focusColor};
  }

  &::-webkit-slider-thumb {
    appearance: none;
    ${rangeThumbStyles}
  }

  &::-moz-range-thumb {
    ${rangeThumbStyles}
  }

  &:hover::-webkit-slider-thumb,
  &:focus-visible::-webkit-slider-thumb {
    height: 16px;
    width: 16px;
  }

  &:hover::-moz-range-thumb,
  &:focus-visible::-moz-range-thumb {
    height: 16px;
    width: 16px;
  }
`;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  background: string;
  isDesktopVideo?: boolean;
  step?: number;
  ref?: RefObject<HTMLInputElement | null>;
}

export const SeekSlider: FC<Props> = ({ ref, ...props }) => {
  const { language } = useEmbedParametersContext();
  return (
    <SeekSliderStyled
      data-testid="seek-slider"
      aria-label={i18n("seek_slider", { language })}
      min={0}
      ref={ref}
      step={props.step ?? 1}
      type="range"
      {...props}
    />
  );
};
